// Oracle online-redefinition planner (v2 Slice H3).
//
// PRD: Ralph/DdlStructuralEdit2/PRD.md § Slice H3 (lifts v1 R5).
//
// Direct `ALTER TABLE … DROP COLUMN` on a large Oracle table is fully blocking
// — the engine rewrites every row, holding an exclusive lock for the duration.
// Oracle's `dbms_redefinition` package exposes an online alternative: build an
// interim table with the post-edit shape, register the redef, copy dependents,
// finish the redef, drop the interim. Only ALTER ops on tables exceeding a
// configurable size threshold (default 1 GiB, consumer-tunable) emit the
// redef sequence; smaller tables stay on the direct ALTER path.
//
// Size probe via `dba_segments.bytes` is documented as the consumer's
// responsibility — they pass `currentTableBytes` alongside the option. When
// `currentTableBytes` is undefined OR below the threshold, the planner returns
// `undefined` to signal "fall through to direct ALTER" (the dispatcher then
// routes to the standard column-edit planner).
//
// Source structure must be `create-table`: the redef path needs the full
// post-edit column shape so it can build the interim table. For an ALTER
// TABLE source the applicator throws with a documented hint.

import { rangeFromOffsets } from '../../../../sql-grammar/document/lineIndex';
import { foldIdentifier } from '../../../scope/identifiers';
import type { ColumnDef, DdlEdit, DdlStructure, PlannedByteEdit } from '../../../types';

import { pickAppendInsertion } from '../appendInsertion';
import type { DdlEditCtx } from '../applyDdlEdit';

type CreateTableStruct = Extract<DdlStructure, { kind: 'create-table' }>;
type DropColumnEdit = Extract<DdlEdit, { op: 'drop-column' }>;

export const ORACLE_REDEF_DEFAULT_THRESHOLD_BYTES: number = 1024 * 1024 * 1024;

export interface OracleOnlineRedefOptions {
  /** Apply the redef path when `currentTableBytes >= sizeThresholdBytes`.
   *  Defaults to 1 GiB (`ORACLE_REDEF_DEFAULT_THRESHOLD_BYTES`). */
  readonly sizeThresholdBytes?: number;
  /** Consumer-supplied probe result. When undefined, the planner falls back
   *  to direct ALTER (treats "no probe" as "small table — don't redef"). */
  readonly currentTableBytes?: number;
}

/** Decide whether the redef path applies and, if so, return the planned
 *  byte edits. Returns `undefined` when the option isn't engaged for this
 *  edit (engine != oracle, op != drop-column, threshold not met) — the
 *  caller falls through to the direct ALTER path. */
export function planOracleOnlineRedef(
  ctx: DdlEditCtx,
  edit: DdlEdit,
  options: OracleOnlineRedefOptions | undefined,
): readonly PlannedByteEdit[] | undefined {
  if (options === undefined) return undefined;
  if (ctx.engine !== 'oracle') return undefined;
  if (edit.op !== 'drop-column') return undefined;
  const threshold = options.sizeThresholdBytes ?? ORACLE_REDEF_DEFAULT_THRESHOLD_BYTES;
  const bytes = options.currentTableBytes;
  if (bytes === undefined || bytes < threshold) return undefined;

  if (ctx.structure.kind !== 'create-table') {
    throw new Error(
      `applyDdlEdit: oracleOnlineRedef requires a 'create-table' source `
        + `structure (got '${ctx.structure.kind}'). The redef path needs the full `
        + `column shape — pass the source CREATE TABLE statement, not an ALTER TABLE.`,
    );
  }
  if (ctx.structure.columns.length === 0) {
    throw new Error(
      `applyDdlEdit: oracleOnlineRedef requires at least one extracted column `
        + `(empty CREATE TABLE column list).`,
    );
  }
  return [planRedefSequence(ctx, ctx.structure, edit)];
}

function planRedefSequence(
  ctx: DdlEditCtx,
  s: CreateTableStruct,
  edit: DropColumnEdit,
): PlannedByteEdit {
  const idx = s.columns.findIndex((c) => c.name === edit.columnName);
  if (idx < 0) {
    throw new Error(
      `applyDdlEdit: oracleOnlineRedef drop-column '${edit.columnName}' not found in column list`,
    );
  }
  const remaining = s.columns.filter((_, i) => i !== idx);
  if (remaining.length === 0) {
    throw new Error(
      `applyDdlEdit: oracleOnlineRedef cannot drop the only column '${edit.columnName}'`,
    );
  }
  const synth = synthesizeRedef(ctx, s, remaining);
  const { offset: insertAt, sep, trailSep } = pickAppendInsertion(
    ctx.source, ctx.statementInfo.range.endOffset,
  );
  return {
    range: rangeFromOffsets(ctx.lineIdx, insertAt, insertAt),
    replacement: sep + synth + trailSep,
    rationale: 'drop-column:oracle-online-redef',
  };
}

function synthesizeRedef(
  ctx: DdlEditCtx,
  s: CreateTableStruct,
  remaining: readonly ColumnDef[],
): string {
  const interimName = `${s.table.name}_int`;
  const interimText = s.table.schema
    ? `${s.table.schema}.${interimName}`
    : interimName;
  // Oracle data-dictionary casing: an UNQUOTED identifier is stored
  // upper-folded, a QUOTED identifier is stored case-exact. The
  // DBMS_REDEFINITION args must name the *dictionary* object, so fold via
  // the canonical helper keyed on the source table's quoting — never an
  // unconditional .toUpperCase() (that misses quoted mixed-case tables).
  const dictName = foldIdentifier(s.table.name, s.table.quoted, ctx.options);
  // The interim table is emitted BARE (see `interimText` — no quote chars
  // are added even when the source was quoted), so an unquoted Oracle
  // identifier `<name>_int` is dictionary-stored upper-folded. Fold it as
  // unquoted regardless of the source's quoting regime to stay consistent
  // with the actual emitted `CREATE TABLE <name>_int` form.
  const dictInterim = foldIdentifier(interimName, /* interim emitted bare — NOT s.table.quoted */ false, ctx.options);
  // KNOWN BROKEN, deliberately left byte-identical to base — and the round-19
  // comment that stood here was wrong about WHY, so this states the real
  // defect. Oracle's column quoting is per COLUMN, never per table: one
  // `CREATE TABLE t (Id NUMBER, "Mixed" NUMBER)` stores `ID` and `Mixed` side
  // by side under an unquoted table. Folding every column with the TABLE's
  // `quoted` flag is therefore wrong for EVERY delimited mixed-case column,
  // not only inside some "mixed-regime" table: under an unquoted table `"Id"`
  // folds to `ID`, and under a quoted one it is emitted bare into the mapping
  // STRING, which DBMS_REDEFINITION parses as SQL and folds to `ID` just the
  // same. Either way the dictionary holds `Id`.
  //
  // Not fixed here because nothing can reach it and nothing can be silently
  // corrupted by it: no product caller sets `oracleOnlineRedef` (only
  // `applyDdlEdit.recreate.test.ts` does), and the interim table is built from
  // each column's VERBATIM source slice, so it holds `Id` while the mapping
  // names `ID` — the redef call fails loudly on a column the interim does not
  // have rather than quietly redefining another one.
  //
  // The fix, the day a caller arrives: select the occurrence with
  // `columnEdit.createTable.ts`'s `declaredColumnIndex` and build the mapping
  // from each column's OWN `nameQuoting` (a delimited one goes in as
  // `"Id" "Id"`), which is the contract every other emitter is already on.
  const colMapping = remaining
    .map((c) => {
      const d = foldIdentifier(c.name, s.table.quoted, ctx.options);
      return `${d} ${d}`;
    })
    .join(', ');
  const colSegments = remaining.map((c) => sliceColumnText(ctx, c));

  // Minimal-scope sequence rationale (SED-15): this generator emits a SINGLE,
  // non-concurrent script run as one unit (CREATE interim → START_REDEF →
  // COPY_TABLE_DEPENDENTS → FINISH_REDEF → DROP interim), so two
  // DBMS_REDEFINITION calls are intentionally omitted:
  //  (1) CAN_REDEF_TABLE — the pre-eligibility check is omitted because
  //      START_REDEF_TABLE itself performs the same eligibility validation and
  //      fails fast with a clear Oracle error if the table cannot be online-
  //      redefined; a separate pre-check would only duplicate that validation.
  //  (2) SYNC_INTERIM_TABLE — the interim re-sync is omitted because it is only
  //      needed for TRUE online redefinition, where the base table receives
  //      concurrent DML during the (potentially long) window between START and
  //      FINISH. This sequence runs START→COPY→FINISH back-to-back with no
  //      live-DML window, so there is nothing to re-sync.
  const stmts: string[] = [
    `CREATE TABLE ${interimText} (${colSegments.join(', ')})`,
    `BEGIN\n`
      + `  DBMS_REDEFINITION.START_REDEF_TABLE(USER, '${dictName}', '${dictInterim}', '${colMapping}');\n`
      + `  DBMS_REDEFINITION.COPY_TABLE_DEPENDENTS(USER, '${dictName}', '${dictInterim}');\n`
      + `  DBMS_REDEFINITION.FINISH_REDEF_TABLE(USER, '${dictName}', '${dictInterim}');\n`
      + `END`,
    `DROP TABLE ${interimText}`,
  ];
  return stmts.join(';\n');
}

function sliceColumnText(ctx: DdlEditCtx, col: ColumnDef): string {
  if (!col.range) {
    throw new Error(
      `applyDdlEdit: oracleOnlineRedef cannot slice column '${col.name}' without a source range`,
    );
  }
  return ctx.source.slice(col.range.startOffset, col.range.endOffset);
}
